import {
  DataTable,
  CheckboxCell,
  CheckboxHeader,
} from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useBackOfficeUsersService,
  useUpdateUserTypeService,
} from '@/services/backoffice';
import type { BackOfficeUserProfile } from '@ditto/shared';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';
import { Search, Settings } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { CreateUserModal } from '../../components/CreateUserModal';
import { UserWorkspaceModal } from '../../components/UserWorkspaceModal';
import { UsersActionBar } from './components/UsersActionBar';

export const UsersPage: FC = () => {
  const { users, isLoading: usersLoading } = useBackOfficeUsersService();
  const { updateUserType } = useUpdateUserTypeService();
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [userWorkspaceModalOpen, setUserWorkspaceModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return users;
    return users.filter(user =>
      `${user.firstName} ${user.lastName} ${user.email}`
        .toLowerCase()
        .includes(userSearchQuery.toLowerCase())
    );
  }, [users, userSearchQuery]);

  const selectedUsers = useMemo(() => {
    return filteredUsers.filter(u => rowSelection[u.id]);
  }, [filteredUsers, rowSelection]);

  const clearSelection = () => {
    setRowSelection({});
  };

  const columns = useMemo<ColumnDef<BackOfficeUserProfile>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => <CheckboxHeader table={table} />,
        cell: ({ row }) => <CheckboxCell row={row} />,
        meta: { headerClassName: 'w-[50px]' },
      },
      {
        accessorKey: 'firstName',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            First Name
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => row.original.firstName || '-',
      },
      {
        accessorKey: 'lastName',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Last Name
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => row.original.lastName || '-',
      },
      {
        accessorKey: 'email',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Email
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => row.original.email || '-',
      },
      {
        id: 'workspaces',
        header: 'Workspaces',
        cell: ({ row }) => {
          const count =
            row.original.workspaceCount ??
            row.original.workspaceIds?.length ??
            0;
          return (
            <span className={count === 0 ? 'text-red-500 font-medium' : ''}>
              {count}
            </span>
          );
        },
      },
      {
        accessorKey: 'locale',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Locale
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => row.original.locale || '-',
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Select
            value={row.original.type ?? 'client'}
            onValueChange={v =>
              updateUserType({
                userId: row.original.id,
                type: v as 'ditto' | 'client' | 'consultant',
              })
            }
          >
            <SelectTrigger className="h-7 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="consultant">Consultant</SelectItem>
              <SelectItem value="ditto">Admin</SelectItem>
            </SelectContent>
          </Select>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedUser(row.original);
                  setUserWorkspaceModalOpen(true);
                }}
              >
                <Settings size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Manage Workspaces</TooltipContent>
          </Tooltip>
        ),
      },
    ],
    [updateUserType, setSelectedUser, setUserWorkspaceModalOpen]
  );

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">All Users</h2>
        <Button onClick={() => setCreateUserModalOpen(true)}>
          Create User
        </Button>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={userSearchQuery}
          onChange={e => setUserSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>
      <DataTable
        columns={columns}
        data={filteredUsers}
        isLoading={usersLoading}
        hidePagination
        options={{
          getRowId: row => row.id,
          enableRowSelection: true,
          onRowSelectionChange: setRowSelection,
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
          enableMultiSort: false,
          state: { rowSelection, sorting },
        }}
      />
      <CreateUserModal
        open={createUserModalOpen}
        onOpenChange={setCreateUserModalOpen}
      />
      <UserWorkspaceModal
        open={userWorkspaceModalOpen}
        onOpenChange={setUserWorkspaceModalOpen}
        user={selectedUser}
      />
      <UsersActionBar
        open={selectedUsers.length > 0}
        selectedUsers={selectedUsers}
        onClearSelection={clearSelection}
      />
    </FlexCol>
  );
};
